import { MenuOpenChangeDetails } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import type { NavigateFunction } from "react-router-dom";

import type { DocDecision, LegalOnboardingDetail } from "../schema/handler-functions";
import { requireChangesLegalOnboardingHandler } from "../schema/handler-functions";
import { mergeRequireChangesText } from "../../schema/auxiliar-handlers";

export const useRequireChangesFlow = ({
  id,
  detail,
  decisions,
  navigate,
  fetchPermissions,
  routeState,
  getValues,
  reset,
  watch,
  showMustStartReviewToast,
  showRequireChangesSuccessToast,
  refreshDetail,
  requireChangesDisabled,
}: {
  id: string | undefined;
  detail: LegalOnboardingDetail | null;
  decisions: Record<number, DocDecision>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  routeState: any;
  getValues: () => any;
  reset: (values: any) => void;
  watch: (name: string) => any;
  showMustStartReviewToast: () => void;
  showRequireChangesSuccessToast: () => void;
  refreshDetail: () => Promise<void>;
  requireChangesDisabled: boolean;
}) => {
  const [openRequireChanges, setOpenRequireChanges] = useState<boolean>(false);
  const [loadingRequireChanges, setLoadingRequireChanges] =
    useState<boolean>(false);

  const requireChangesDetail = watch("require_changes_detail");

  const requireChangesTemplate = useMemo(() => {
    const rejectedDocs = (detail?.documentos ?? []).filter(
      (d: any) => decisions?.[d.id] === "reject",
    );
    if (!rejectedDocs.length) return "";

    return rejectedDocs
      .map(
        (d: any) =>
          `- ${d?.nombre ?? "Documento"}\nBORRE ESTE TEXTO E INGRESE JUSTIFICACIONES`,
      )
      .join("\n\n");
  }, [detail?.documentos, decisions]);

  const onOpenChangeRequireChanges = (e: MenuOpenChangeDetails) => {
    if (e.open) {
      const current = getValues()?.require_changes_detail ?? "";
      const merged = mergeRequireChangesText(current, requireChangesTemplate);
      if (merged !== current) {
        reset({ require_changes_detail: merged });
      }
    } else {
      reset({ require_changes_detail: "" });
    }
    setOpenRequireChanges(e.open);
  };

  const onConfirmRequireChanges = async () => {
    const text = (getValues()?.require_changes_detail ?? "").trim();
    if (!text.length) return;
    if (!id) return;

    const filteredDocuments = detail?.documentos?.filter((d: any) => Boolean(d.idDocumento))?? [];

    try {
      setLoadingRequireChanges(true);
      await requireChangesLegalOnboardingHandler({
        id: Number(id),
        detail: text,
        documentos: filteredDocuments ?? [],
        decisions,
        navigate,
        fetchPermissions,
        routeState,
      });
      setOpenRequireChanges(false);
      showRequireChangesSuccessToast();
      await refreshDetail();
    } catch {
      // handler already handles permission + error toast
    } finally {
      setLoadingRequireChanges(false);
    }
  };

  const onRequireChanges = () => {
    if (requireChangesDisabled) {
      showMustStartReviewToast();
      return;
    }
    const current = (getValues()?.require_changes_detail ?? "").trim();
    if (!current.length && requireChangesTemplate.trim().length) {
      reset({ require_changes_detail: requireChangesTemplate });
    }
    setOpenRequireChanges(true);
  };

  return {
    openRequireChanges,
    loadingRequireChanges,
    requireChangesDetail,
    onOpenChangeRequireChanges,
    onConfirmRequireChanges,
    onRequireChanges,
  };
};
